import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { UserService } from 'src/app/api/services';
import { ActivatedRoute, Router } from '@angular/router';
import { JobOfferPostulantService } from 'src/app/api/services/job-offer-postulant.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { Profile } from 'src/app/shared/model/user';
import { UserExperienceService } from 'src/app/api/services/user-experience.service';
import { featherExternalLink, featherMail } from '@ng-icons/feather-icons';
import { heroCalendarDays } from '@ng-icons/heroicons/outline';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { StatusProcess } from 'src/app/core/enum/status-process';
import { NavButtonComponent } from 'src/app/shared/component/nav-button/nav-button.component';
import { JobOfferPostulantDetail } from 'src/app/shared/model/job-offer-postulant';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { APP_CONSTANTS } from 'src/app/core/constant/constants';

@Component({
  selector: 'app-user-company-view',
  imports: [
    CommonModule,
    NgIcon,
    LoadingButtonComponent,
    NavButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './user-company-view.component.html',
  viewProviders: [
    provideIcons({
      featherMail,
      featherExternalLink,
      heroCalendarDays,
    }),
  ],
})
export class UserCompanyViewComponent implements OnInit {
  user!: Profile;
  experiences: UserExperienceResponse[] = [];
  details!: JobOfferPostulantDetail;
  idJobOffer!: string | null;
  confirmModalOpen: boolean = false;
  isLoading: boolean = true;
  isLoadingStatusProcess: boolean = false;
  actionType: 'revisar' | 'preseleccionar' | 'seleccionar' | null = null;

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private toastService: ToastService,
    private router: Router,
    private jobOfferPostulantService: JobOfferPostulantService,
    private userExpService: UserExperienceService
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.idJobOffer = this.route.snapshot.paramMap.get('id');

    if (this.idJobOffer) {
      this.jobOfferPostulantService
        .getPostulantById(this.idJobOffer)
        .subscribe({
          next: (response) => {
            this.details = response.dto.jobOfferPostulant;
            this.userService
              .getProfile(response.dto.jobOfferPostulant.idUser)
              .subscribe({
                next: (response) => {
                  this.user = response.dto.user;
                  this.userExpService
                    .getExperiencesByUser(this.user.idUser)
                    .subscribe({
                      next: (res) => {
                        this.experiences = res.dto.experiences || [];
                        this.isLoading = false;
                      },
                      error: (err) => {
                        this.experiences = [];
                        this.isLoading = false;
                      },
                    });
                },
                error: (err) => {
                  this.toastService.showMessages(
                    'error',
                    err.error?.listMessage || [MESSAGES.GENERAL.UNKNOWN_ERROR]
                  );
                  this.router.navigate(['/user/company']);
                  this.isLoading = false;
                },
              });
          },
          error: (err) => {
            this.toastService.showMessages(
              'error',
              err.error?.listMessage || [MESSAGES.POSTULANT.GET_ERROR]
            );
            this.router.navigate(['/user/company']);
            this.isLoading = false;
          },
        });
    } else {
      this.toastService.showMessages('error', [MESSAGES.USER.GET_ERROR]);
    }
  }

  openConfirmModal(action: 'revisar' | 'preseleccionar' | 'seleccionar') {
    this.actionType = action;
    this.confirmModalOpen = true;
  }

  closeConfirmModal() {
    this.confirmModalOpen = false;
  }

  confirmAction() {
    if (!this.actionType || !this.details) return;

    let status: StatusProcess;

    switch (this.actionType) {
      case 'revisar':
        status = StatusProcess.Revisado;
        break;
      case 'preseleccionar':
        status = StatusProcess.Preseleccionado;
        break;
      case 'seleccionar':
        status = StatusProcess.Seleccionado;
        break;
      default:
        return;
    }

    this.isLoadingStatusProcess = true;

    this.jobOfferPostulantService
      .updateStatusProcessByIdJobOfferPostulant(
        this.details.idJobOfferPostulant,
        status
      )
      .subscribe({
        next: () => {
          this.toastService.showMessages('success', [
            `Postulante marcado como ${status}`,
          ]);
          this.details.statusProcess = status;

          this.isLoadingStatusProcess = false;
          this.closeConfirmModal();
        },
        error: (err) => {
          this.isLoadingStatusProcess = false;
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || [
              MESSAGES.POSTULANT.UPDATE_POSTULANT_JOB_ERROR,
            ]
          );
        },
      });
  }

  getNextAction(details: any): 'revisar' | 'preseleccionar' | 'seleccionar' {
    switch (details.statusProcess) {
      case 'Enviado':
        return 'revisar';
      case 'Revisado':
        return 'preseleccionar';
      case 'Preseleccionado':
        return 'seleccionar';
      default:
        return 'seleccionar';
    }
  }

  getNextActionLabel(details: any): string {
    switch (details.statusProcess) {
      case 'Enviado':
        return 'Marcar como revisado';
      case 'Revisado':
        return 'Marcar como preseleccionado';
      case 'Preseleccionado':
        return 'Marcar como seleccionado';
      case 'Seleccionado':
        return 'Usuario seleccionado';
      default:
        return 'Marcar';
    }
  }

  getStatusLabel(details: any): string {
    switch (details.statusProcess) {
      case 'Enviado':
        return 'Nueva postulación';
      case 'Revisado':
        return 'Perfil revisado';
      case 'Preseleccionado':
        return 'Preseleccionado';
      case 'Seleccionado':
        return 'Seleccionado';
      default:
        return 'Desconocido';
    }
  }

  get navRoute() {
    return `/user/company/${this.details.job_offer.company_package.company.idCompany}/job-offer-details/${this.details.idJobOffer}`;
  }

  userImage(): string {
    if (!this.user) {
      return APP_CONSTANTS.DEFAULT_IMAGE_PROFILE;
    }
    const url = this.user.urlAvatar;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_IMAGE_PROFILE;
    }
    return url;
  }
}
